import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router";
import i18n from "./i18n/i18n";

// Provides custom fonts
import "@inductiveautomation/ws-components/style.css";

// Provides augmented types for the MUI theme
import "@inductiveautomation/ws-components/themeExtensions";

import {
  ApiErrorHandler,
  ConfiguredAuth0Provider,
  CustomAuth0Provider,
  LaunchDarklyProvider,
  LoadingPage,
  SnackbarProvider,
  initGTM,
  loadFavicons,
} from "@inductiveautomation/ws-components";

import { AppState } from "@auth0/auth0-react";
import CustomComponentProvider from "./contexts/component";
import { store } from "./contexts/endpoints/store";
import router from "./router";
import {
  CHANNEL_MESSAGE_TYPES,
  retrieveAuthChannel,
} from "./utilities/broadcastChannel";
import { setAccountRedirectUrl } from "./utilities/redirectUrl";

const auth0Config = {
  AUTH0_DOMAIN: window._acctConfig?.AUTH0_DOMAIN ?? "",
  AUTH0_CLIENT_ID: window._acctConfig?.AUTH0_CLIENT_ID ?? "",
  AUTH0_CALLBACK_URL: window._acctConfig?.AUTH0_CALLBACK_URL ?? "",
  onRedirectCallback: (appState?: AppState) => {
    const { sendAuthChannelMessage } = retrieveAuthChannel();
    setAccountRedirectUrl(appState);
    sendAuthChannelMessage(CHANNEL_MESSAGE_TYPES.LOGIN);
  },
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = document.getElementById("root")!;

if (window._acctConfig?.ENVIRONMENT === "production") {
  initGTM();
}

if (window._acctConfig?.BASE_ASSET_URL) {
  loadFavicons(window._acctConfig.BASE_ASSET_URL);
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <Provider store={store}>
      {/* Wrap everything in I18nextProvider to ensure i18n is available */}
      <I18nextProvider i18n={i18n}>
        <Suspense
          fallback={
            <LoadingPage
              baseAssetUrl={window._acctConfig?.BASE_ASSET_URL ?? ""}
            />
          }
        >
          <ConfiguredAuth0Provider auth0Config={auth0Config}>
            <CustomAuth0Provider>
              <LaunchDarklyProvider
                clientSideID={window._acctConfig?.LAUNCHDARKLY_CLIENT_ID ?? ""}
              >
                <CustomComponentProvider>
                  <SnackbarProvider>
                    <ApiErrorHandler />
                    <RouterProvider
                      //fallbackElement={<LoadingPage />}
                      router={router}
                    />
                  </SnackbarProvider>
                </CustomComponentProvider>
              </LaunchDarklyProvider>
            </CustomAuth0Provider>
          </ConfiguredAuth0Provider>
        </Suspense>
      </I18nextProvider>
    </Provider>
  </React.StrictMode>
);
